import Phaser from 'phaser';
import { MULTISHOT, SHOP } from '../config/GameConfig';
import type Player from '../entities/Player';
import type SoundManager from './SoundManager';

export type RarityKey = 'common' | 'rare' | 'epic' | 'legendary';

export interface RarityConfig {
  label: string;
  color: string;
  borderRgba: string;
  bgRgba: string;
  weight: number;
  /** 星级 1~4：直观告诉玩家"几颗星越多越强" */
  stars: number;
}

/**
 * 稀有度权重（再次下调）—— 用户反馈"好技能太常见破坏平衡"：
 *   common  78  ← 普通主导
 *   rare    18
 *   epic     3.5
 *   legendary 0.5  ← 千里挑一
 *
 * 期望：3 张牌里至少 1 张稀有以上的概率 ≈ 53%（之前 ≈ 73%）
 *       任意一张抽到史诗+ 的概率 ≈ 4%（之前 ≈ 9%）
 *       任意一张抽到传说的概率 ≈ 0.5%（之前 ≈ 1%）
 */
export const RARITY: Record<RarityKey, RarityConfig> = {
  common:    { label: '普通', color: '#cfd6e0', borderRgba: 'rgba(207,214,224,.55)', bgRgba: 'rgba(207,214,224,.10)', weight: 78,  stars: 1 },
  rare:      { label: '稀有', color: '#4cd2ff', borderRgba: 'rgba(76,210,255,.65)',  bgRgba: 'rgba(76,210,255,.12)',  weight: 18,  stars: 2 },
  epic:      { label: '史诗', color: '#c084ff', borderRgba: 'rgba(192,132,255,.7)',  bgRgba: 'rgba(192,132,255,.14)', weight: 3.5, stars: 3 },
  legendary: { label: '传说', color: '#ffb84a', borderRgba: 'rgba(255,184,74,.75)',  bgRgba: 'rgba(255,184,74,.15)',  weight: 0.5, stars: 4 },
};

/** 拼出 "★★★☆"（n 颗实心 + 4-n 颗空心），用 RARITY.color 给实心着色 */
function _starsHtml(n: number, color: string): string {
  const filled = '★'.repeat(n);
  const empty  = '☆'.repeat(Math.max(0, 4 - n));
  return `<span style="color:${color}; letter-spacing:1px;">${filled}</span><span style="color:#3a4760; letter-spacing:1px;">${empty}</span>`;
}

export type UpgradeType =
  | 'fireRate'
  | 'damage'
  | 'maxHp'
  | 'heal'
  | 'bulletCount'
  | 'pierce'
  | 'homing'
  | 'shield'
  | 'nuke'
  | 'crit'
  | 'lifesteal'
  | 'ricochet'
  | 'spread'
  | 'berserk'
  | 'shieldExplode';

export interface Upgrade {
  id: string;
  name: string;
  desc: string;
  rarity: RarityKey;
  type: UpgradeType;
  value: number | boolean;
  /** 一次性词条：拾取后从全局词条池中永久剔除（如 homing 这种 boolean 开关） */
  unique?: boolean;
}

/**
 * 词条强度调整记录（本次平衡 patch）：
 *  - 整体下调每张卡的数值，让"成型时间"晚 2-3 分钟
 *  - 关键开关型/百分比型加 unique 防止连乘破天
 *  - 射速下限 60→100ms，避免后期一秒 15+ 发把节奏打废
 *
 * 稀有度权重也微调：传说从 2 → 1，史诗从 10 → 8，让超强卡更难白嫖
 */
export const UPGRADE_POOL: Upgrade[] = [
  // 普通
  { id: 'firerate-1', name: '强力速射', desc: '射击间隔 -8%',  rarity: 'common', type: 'fireRate', value: -0.08 },
  { id: 'damage-1',   name: '强化弹药', desc: '子弹伤害 +1',   rarity: 'common', type: 'damage',   value: 1 },
  { id: 'heal-1',     name: '紧急维修', desc: '当前生命 +2',   rarity: 'common', type: 'heal',     value: 2 },
  { id: 'multishot-1',name: '副管炮塔', desc: '同时发射 +1',   rarity: 'common', type: 'bulletCount', value: 1 },

  // 稀有
  { id: 'firerate-2', name: '极速连射', desc: '射击间隔 -15%', rarity: 'rare', type: 'fireRate', value: -0.15 },
  { id: 'damage-2',   name: '穿甲弹头', desc: '子弹伤害 +2',   rarity: 'rare', type: 'damage',   value: 2 },
  { id: 'maxhp-1',    name: '强健装甲', desc: '最大生命 +2 并补满', rarity: 'rare', type: 'maxHp', value: 2 },
  { id: 'multishot-2',name: '双联火力', desc: '同时发射 +2',   rarity: 'rare', type: 'bulletCount', value: 2 },
  { id: 'pierce-1',   name: '贯穿弹头', desc: '子弹可继续命中后方 1 个敌人',   rarity: 'rare', type: 'pierce',   value: 1 },
  { id: 'shield-1',   name: '能量护盾', desc: '获得 1 层护盾',  rarity: 'rare', type: 'shield',   value: 1 },
  { id: 'crit-1',     name: '精准瞄准', desc: '暴击率 +12%（暴击 ×2 伤害）', rarity: 'rare', type: 'crit', value: 0.12 },
  { id: 'lifesteal-1',name: '吸血弹头', desc: '击杀 12% 概率回 1 血', rarity: 'rare', type: 'lifesteal', value: 0.12 },

  // 史诗
  { id: 'multishot-3',name: '弹幕狂热', desc: '同时发射 +3',   rarity: 'epic', type: 'bulletCount', value: 3 },
  // pierce-2 标 unique:穿透是几何级别的清屏增益,叠两张就能横切整列
  { id: 'pierce-2',   name: '钢芯穿甲', desc: '子弹可继续命中后方 2 个敌人（独有）', rarity: 'epic', type: 'pierce', value: 2, unique: true },
  { id: 'shield-2',   name: '双重护盾', desc: '获得 2 层护盾',  rarity: 'epic', type: 'shield',   value: 2 },
  { id: 'damage-3',   name: '末日弹头', desc: '子弹伤害 +4',   rarity: 'epic', type: 'damage',   value: 4 },
  { id: 'crit-2',     name: '致命狙击', desc: '暴击率 +20%', rarity: 'epic', type: 'crit', value: 0.20 },
  { id: 'lifesteal-2',name: '嗜血协议', desc: '击杀 25% 概率回 1 血', rarity: 'epic', type: 'lifesteal', value: 0.13 },
  // ricochet：一次性反弹是个强位移增益，多张可叠
  { id: 'ricochet-1', name: '反弹弹头', desc: '子弹命中后会折向最近敌人 +1 次', rarity: 'epic', type: 'ricochet', value: 1 },

  // 传说
  // homing 是 boolean 开关，重复拾取没意义
  { id: 'homing-1',   name: '导引模块', desc: '子弹获得追踪能力（独有）', rarity: 'legendary', type: 'homing', value: true, unique: true },
  // firerate-3 标 unique：百分比连乘 + 抽到一张就顶下限，太破坏节奏
  { id: 'firerate-3', name: '神速射击', desc: '射击间隔 -25%（独有）', rarity: 'legendary', type: 'fireRate', value: -0.25, unique: true },
  { id: 'maxhp-2',    name: '钛合金身', desc: '最大生命 +3 并补满', rarity: 'legendary', type: 'maxHp', value: 3 },
  // 一次性大招：选中后立刻清空全屏所有怪物（含 Boss）和 Boss 弹幕
  // 不标 unique —— 让玩家有机会再抽到再清一次（真正的"一次性技能"语义）
  { id: 'nuke-1',     name: '核爆指令', desc: '立即清除全屏所有敌人（一次性）', rarity: 'legendary', type: 'nuke', value: true },

  // ── 流派定型卡（unique，每张定义一种打法风格）────────────────────
  // spread：精度换覆盖，配合 multishot 变成"霰弹清屏"
  { id: 'spread-1',   name: '霰弹专精', desc: '每发子弹随机偏转 ±15°，配合多重射击形成弹幕扫射（独有）', rarity: 'legendary', type: 'spread', value: 0.26, unique: true },
  // berserk：奖励"堆射速到 100ms"流派 —— 拿了之后所有提射速的卡都更值
  { id: 'berserk-1',  name: '速射狂热', desc: '射速 ≤ 100ms 时伤害 +30%（独有）', rarity: 'legendary', type: 'berserk', value: true, unique: true },
  // shieldExplode：tank 流派的关键开关，配合护盾叠层
  { id: 'shieldex-1', name: '盾爆协议', desc: '护盾被打碎时炸 120 半径（伤害 = 子弹伤害 ×5；独有）', rarity: 'legendary', type: 'shieldExplode', value: true, unique: true },
];

/**
 * 按当前波次（tier）计算各稀有度的实际权重。
 *
 * 设计：开局尽量不出强卡（让玩家先靠普通卡熬过去），
 * 后期强卡概率"线性爬坡 + 上限封顶"，避免无止境膨胀。
 *
 * 公式：actualWeight = baseWeight + tier × tierBoost，clamped 到 maxWeight
 *
 * 实际数表（举例 baseWeight 看上面 RARITY 定义）：
 *   tier=0  → common 78  / rare 18    / epic 3.5  / legendary 0.5
 *   tier=3  → common 78  / rare 27    / epic 8    / legendary 2.0   （3×增长）
 *   tier=6  → common 78  / rare 36    / epic 12.5 / legendary 3.5
 *   tier≥10 → common 78  / rare 50(封)/ epic 20(封)/ legendary 6(封)
 *
 * 这样：开局一张传说概率 ~0.3%，10 波后 ~3.6%（12 倍提升）；
 * 普通卡权重不动，所以"普通主导"的体感前后期都在，只是后期"被强卡稀释"。
 */
const RARITY_GROWTH: Record<RarityKey, { tierBoost: number; maxWeight: number }> = {
  common:    { tierBoost: 0,    maxWeight: 78 },
  rare:      { tierBoost: 3.2,  maxWeight: 50 },
  epic:      { tierBoost: 1.5,  maxWeight: 20 },
  legendary: { tierBoost: 0.55, maxWeight: 6  },
};

function _rarityWeight(rarity: RarityKey, tier: number): number {
  const base = RARITY[rarity]?.weight ?? 1;
  const g = RARITY_GROWTH[rarity];
  if (!g) return base;
  return Math.min(g.maxWeight, base + Math.max(0, tier) * g.tierBoost);
}

function _weightedPick(pool: Upgrade[], tier: number): Upgrade {
  const total = pool.reduce((s, c) => s + _rarityWeight(c.rarity, tier), 0);
  let r = Math.random() * total;
  for (const c of pool) {
    r -= _rarityWeight(c.rarity, tier);
    if (r <= 0) return c;
  }
  return pool[pool.length - 1];
}

export interface UpgradeHooks {
  onOpen?: () => void;
  onClose?: () => void;
  sound?: SoundManager;
  /**
   * 抽卡时实时拿当前波次（tier，从 0 开始）。
   * 返回值喂给 _weightedPick：tier 越高，高稀有度卡权重越大。
   * 不传则默认按 tier=0 处理（相当于关闭"波次提升强卡概率"）。
   */
  getTier?: () => number;
  /** 选中"核爆指令"等清屏类词条时触发 —— 由 MainScene 实际清场（Player 不知道 enemyGroup） */
  onNuke?: () => void;
  /** 任意改了 player.hp 的地方（heal 词条 / 商店买回血）都会触发，让 HUD 同步刷新 */
  onHeal?: () => void;
}

/**
 * UpgradeManager
 *
 * - 每收集 threshold 个宝箱触发一次面板（暂停物理 + 难度）
 * - 面板里有 3 个随机词条 + 商店区（重摇 / 立即回血）
 */
export default class UpgradeManager {
  scene: Phaser.Scene;
  player: Player;
  hooks: UpgradeHooks;
  sound: SoundManager | null;

  chestCollected = 0;
  /**
   * 触发升级面板的宝箱阈值。回到 3：
   * 升级节奏靠"稀有度权重"控制（好技能更难抽到），而不是拉长触发间隔
   */
  threshold = 3;

  /** 已被拾取过的 unique 词条 id —— 抽卡时过滤掉（如导引模块） */
  private _consumedUniques = new Set<string>();

  private _panelEl: HTMLElement | null = null;
  private _isOpen = false;
  private _currentChoices: Upgrade[] | null = null;
  private _rerollCost: number = SHOP.rerollCost;
  private _blessingCost: number = SHOP.blessingCost;

  constructor(scene: Phaser.Scene, player: Player, hooks: UpgradeHooks = {}) {
    this.scene = scene;
    this.player = player;
    this.hooks = hooks;
    this.sound = hooks.sound ?? null;
  }

  onChestCollected(): void {
    this.chestCollected += 1;
    if (this.chestCollected >= this.threshold) {
      this.chestCollected = 0;
      this.open();
    }
  }

  isOpen(): boolean { return this._isOpen; }

  open(): void {
    if (this._isOpen) return;
    this._isOpen = true;
    this.scene.physics.pause();
    this.hooks.onOpen?.();
    this.sound?.upgrade();
    this._currentChoices = this._rollChoices();
    this._rerollCost = SHOP.rerollCost;
    this._blessingCost = SHOP.blessingCost;
    this._renderPanel();
  }

  close(): void {
    if (!this._isOpen) return;
    this._isOpen = false;
    this._destroyPanel();
    this.scene.physics.resume();
    this.hooks.onClose?.();
  }

  /**
   * 按权重抽 3 张不重复的词条；过滤掉已被拾取过的 unique 词条
   * （比如已经拿过的"导引模块"不再出现在词条池里）。
   * 权重随当前波次（tier）放大高稀有度卡概率，由 hooks.getTier 提供。
   */
  private _rollChoices(): Upgrade[] {
    const tier = Math.max(0, this.hooks.getTier?.() ?? 0);
    const pool = UPGRADE_POOL.filter((u) => !(u.unique && this._consumedUniques.has(u.id)));
    const out: Upgrade[] = [];
    for (let i = 0; i < 3 && pool.length > 0; i++) {
      const pick = _weightedPick(pool, tier);
      out.push(pick);
      const idx = pool.indexOf(pick);
      if (idx >= 0) pool.splice(idx, 1);
    }
    return out;
  }

  /**
   * 祝福抽：3 张里至少 1 张稀有（含）以上 —— 强化版重抽
   * 实现：先按权重抽 1 张稀有+（rare 起跳），其余 2 张照常滚
   */
  private _rollBlessedChoices(): Upgrade[] {
    const tier = Math.max(0, this.hooks.getTier?.() ?? 0);
    const pool = UPGRADE_POOL.filter((u) => !(u.unique && this._consumedUniques.has(u.id)));
    const out: Upgrade[] = [];
    // 第一张：从 rare/epic/legendary 池里按权重抽
    const rarePool = pool.filter((u) => u.rarity !== 'common');
    if (rarePool.length > 0) {
      const pick = _weightedPick(rarePool, tier);
      out.push(pick);
      const idx = pool.indexOf(pick);
      if (idx >= 0) pool.splice(idx, 1);
    }
    // 后两张：正常滚
    for (let i = out.length; i < 3 && pool.length > 0; i++) {
      const pick = _weightedPick(pool, tier);
      out.push(pick);
      const idx = pool.indexOf(pick);
      if (idx >= 0) pool.splice(idx, 1);
    }
    return out;
  }

  applyUpgrade(upgrade: Upgrade): void {
    if (upgrade.unique) {
      this._consumedUniques.add(upgrade.id);
    }
    const p = this.player;
    switch (upgrade.type) {
      case 'fireRate':
        // 射速下限 100ms（10 发/秒）—— 60ms 时一秒 16+ 发会把整个屏幕压成弹幕墙，
        // 配合 multishot/pierce 直接秒所有怪，把"打 boss/精英"的紧张感打没了
        p.shootingDelay = Math.max(100, Math.round(p.shootingDelay * (1 + (upgrade.value as number))));
        break;
      case 'damage':
        p.bulletDamage += upgrade.value as number;
        break;
      case 'maxHp':
        p.maxHp += upgrade.value as number;
        p.hp = p.maxHp;
        this.hooks.onHeal?.();
        break;
      case 'heal':
        p.hp = Math.min(p.maxHp, p.hp + (upgrade.value as number));
        this.hooks.onHeal?.();
        break;
      case 'bulletCount':
        p.bulletCount = Math.min(MULTISHOT.max, (p.bulletCount || 1) + (upgrade.value as number));
        break;
      case 'pierce':
        p.bulletPierce = (p.bulletPierce || 0) + (upgrade.value as number);
        break;
      case 'homing':
        p.bulletHoming = !!upgrade.value;
        break;
      case 'shield':
        p.shieldCharges = (p.shieldCharges || 0) + (upgrade.value as number);
        break;
      case 'crit':
        // 暴击率封顶 80%（避免 100% 暴击让数值失衡）
        p.critChance = Math.min(0.8, (p.critChance || 0) + (upgrade.value as number));
        break;
      case 'lifesteal':
        // 吸血概率封顶 70%
        p.lifestealChance = Math.min(0.7, (p.lifestealChance || 0) + (upgrade.value as number));
        break;
      case 'ricochet':
        // 反弹次数：每张 +N，没有上限（5 次以上其实意义不大，但不锁）
        p.bulletRicochet = (p.bulletRicochet || 0) + (upgrade.value as number);
        break;
      case 'spread':
        // 霰弹专精：偏转弧度直接覆盖（unique，只会触发一次）
        p.bulletSpread = upgrade.value as number;
        break;
      case 'berserk':
        p.berserk = !!upgrade.value;
        break;
      case 'shieldExplode':
        p.shieldExplode = !!upgrade.value;
        break;
      case 'nuke':
        // 实际清场由 MainScene 拿着 enemyGroup / bossBulletGroup 处理
        this.hooks.onNuke?.();
        break;
      default: {
        const _exhaust: never = upgrade.type;
        console.warn('[UpgradeManager] 未知词条类型', _exhaust);
      }
    }
  }

  /**
   * 算出"当前 → 升级后"的对比字符串，给卡片底部显示
   * 返回 null = 这张卡没有数值变化（或纯 boolean 开关 / 一次性触发）
   *
   * 与 applyUpgrade 必须保持同步 —— 改了那边的逻辑这里也要跟着改
   */
  private _previewDelta(upgrade: Upgrade): { before: string; after: string } | null {
    const p = this.player;
    const v = upgrade.value;
    switch (upgrade.type) {
      case 'fireRate': {
        const cur = Math.round(p.shootingDelay);
        const next = Math.max(100, Math.round(p.shootingDelay * (1 + (v as number))));
        return { before: `${cur}ms`, after: `${next}ms` };
      }
      case 'damage':
        return { before: `${p.bulletDamage}`, after: `${p.bulletDamage + (v as number)}` };
      case 'maxHp':
        return { before: `${p.hp}/${p.maxHp}`, after: `${p.maxHp + (v as number)}/${p.maxHp + (v as number)}` };
      case 'heal':
        return { before: `${p.hp}/${p.maxHp}`, after: `${Math.min(p.maxHp, p.hp + (v as number))}/${p.maxHp}` };
      case 'bulletCount': {
        const next = Math.min(MULTISHOT.max, (p.bulletCount || 1) + (v as number));
        return { before: `${p.bulletCount}`, after: `${next}` };
      }
      case 'pierce':
        return { before: `追加${p.bulletPierce}个`, after: `追加${p.bulletPierce + (v as number)}个` };
      case 'shield':
        return { before: `${p.shieldCharges}`, after: `${p.shieldCharges + (v as number)}` };
      case 'crit': {
        const next = Math.min(0.8, (p.critChance || 0) + (v as number));
        return { before: `${Math.round((p.critChance || 0) * 100)}%`, after: `${Math.round(next * 100)}%` };
      }
      case 'lifesteal': {
        const next = Math.min(0.7, (p.lifestealChance || 0) + (v as number));
        return { before: `${Math.round((p.lifestealChance || 0) * 100)}%`, after: `${Math.round(next * 100)}%` };
      }
      case 'ricochet':
        return { before: `反${p.bulletRicochet}`, after: `反${p.bulletRicochet + (v as number)}` };
      // 纯开关 / 一次性 —— 没有数值前后，返回 null 由 UI 隐藏对比行
      case 'homing':
      case 'spread':
      case 'berserk':
      case 'shieldExplode':
      case 'nuke':
        return null;
      default: {
        const _exhaust: never = upgrade.type;
        void _exhaust;
        return null;
      }
    }
  }

  private _doReroll(): boolean {
    if (this.player.coins < this._rerollCost) return false;
    this.player.coins -= this._rerollCost;
    this._rerollCost += SHOP.rerollCostInc;
    this._currentChoices = this._rollChoices();
    this.sound?.click();
    this._renderPanel();
    return true;
  }
  /** 祝福抽：花更多金币，保证至少 1 张稀有以上 */
  private _doBlessing(): boolean {
    if (this.player.coins < this._blessingCost) return false;
    this.player.coins -= this._blessingCost;
    this._blessingCost += SHOP.blessingCostInc;
    this._currentChoices = this._rollBlessedChoices();
    this.sound?.upgrade();
    this._renderPanel();
    return true;
  }
  private _doHeal(): boolean {
    if (this.player.coins < SHOP.healCost) return false;
    if (this.player.hp >= this.player.maxHp) return false;
    this.player.coins -= SHOP.healCost;
    this.player.hp = Math.min(this.player.maxHp, this.player.hp + SHOP.healAmount);
    this.hooks.onHeal?.();
    this.sound?.coin();
    this._renderPanel();
    return true;
  }

  private _destroyPanel(): void {
    if (this._panelEl?.parentNode) {
      this._panelEl.parentNode.removeChild(this._panelEl);
    }
    this._panelEl = null;
  }

  private _renderPanel(): void {
    this._destroyPanel();
    if (!this._currentChoices) return;
    this._panelEl = this._buildPanelDom(this._currentChoices);
    document.body.appendChild(this._panelEl);
  }

  private _buildPanelDom(choices: Upgrade[]): HTMLElement {
    const overlay = document.createElement('div');
    overlay.style.cssText = `
      position: fixed; inset: 0;
      display: flex; align-items: center; justify-content: center;
      background: rgba(0, 0, 0, 0.72);
      backdrop-filter: blur(3px);
      z-index: 10000;
      font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
    `;

    const panel = document.createElement('div');
    panel.style.cssText = `
      position: relative;
      width: min(90vw, 400px);
      padding: 20px 18px 16px;
      border-radius: 18px;
      background: linear-gradient(180deg, #1d2a44 0%, #0e1424 100%);
      box-shadow:
        0 18px 60px rgba(0,0,0,.65),
        0 0 0 1px rgba(76,210,255,.28) inset,
        0 0 34px rgba(76,210,255,.12);
      color: #e8f1ff;
    `;
    overlay.appendChild(panel);

    const header = document.createElement('div');
    header.style.cssText = `
      display: flex; justify-content: space-between; align-items: center;
      margin-bottom: 12px;
    `;
    const title = document.createElement('div');
    title.textContent = '选择一项升级';
    title.style.cssText = `font-size: 18px; font-weight: 600; color: #4cd2ff;`;
    const coinDisplay = document.createElement('div');
    coinDisplay.textContent = `金币 ${this.player.coins}`;
    coinDisplay.style.cssText = `font-size: 15px; color: #ffe066;`;
    header.appendChild(title);
    header.appendChild(coinDisplay);
    panel.appendChild(header);

    choices.forEach((upgrade) => {
      const r = RARITY[upgrade.rarity] ?? RARITY.common;
      const card = document.createElement('button');
      card.type = 'button';
      card.style.cssText = `
        position: relative;
        display: block; width: 100%;
        margin: 9px 0; padding: 13px 14px;
        text-align: left;
        border: 1.5px solid ${r.borderRgba};
        border-radius: 12px;
        background: linear-gradient(180deg, ${r.bgRgba}, rgba(255,255,255,.035));
        color: #e8f1ff;
        font-size: 15px; line-height: 1.4;
        cursor: pointer;
        box-shadow: 0 0 0 0 ${r.borderRgba}, 0 8px 22px rgba(0,0,0,.18);
        transition: box-shadow .2s ease, transform .12s ease, border-color .12s ease;
      `;
      const preview = this._previewDelta(upgrade);
      const previewHtml = preview
        ? `<div style="margin-top:6px; font-size:12px; color:#aac4d8; display:flex; align-items:center; gap:6px;">
             <span style="opacity:.75;">${preview.before}</span>
             <span style="color:${r.color};">→</span>
             <span style="font-weight:600; color:${r.color};">${preview.after}</span>
           </div>`
        : '';
      card.innerHTML = `
        <div style="display:flex; justify-content:space-between; align-items:center; margin-bottom:4px;">
          <div style="font-weight:600; color:${r.color};">${upgrade.name}</div>
          <div style="display:flex; align-items:center; gap:6px;">
            <span style="font-size:14px;">${_starsHtml(r.stars, r.color)}</span>
            <span style="font-size:11px; padding:1px 6px; border-radius:8px;
                         background:${r.bgRgba}; color:${r.color};
                         border:1px solid ${r.borderRgba}; letter-spacing:.05em;">
              ${r.label}
            </span>
          </div>
        </div>
        <div style="font-size:13px; color:#aac4d8;">${upgrade.desc}</div>
        ${previewHtml}
      `;
      if (upgrade.rarity === 'legendary') {
        card.style.boxShadow = `0 0 12px ${r.borderRgba}`;
      }
      card.addEventListener('pointerenter', () => {
        card.style.transform = 'translateY(-1px)';
        card.style.boxShadow = `0 0 16px ${r.borderRgba}, 0 10px 26px rgba(0,0,0,.24)`;
      });
      card.addEventListener('pointerleave', () => {
        card.style.transform = 'translateY(0)';
        card.style.boxShadow = upgrade.rarity === 'legendary'
          ? `0 0 12px ${r.borderRgba}`
          : `0 0 0 0 ${r.borderRgba}, 0 8px 22px rgba(0,0,0,.18)`;
      });
      card.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.sound?.upgrade();
        this.applyUpgrade(upgrade);
        this.close();
      });
      panel.appendChild(card);
    });

    // 商店：第一行 重抽 + 祝福抽，第二行 治疗（独占一行更醒目）
    const shopRow1 = document.createElement('div');
    shopRow1.style.cssText = `
      display: flex; gap: 8px;
      margin-top: 12px; padding-top: 12px;
      border-top: 1px dashed rgba(76,210,255,.25);
    `;
    const shopRow2 = document.createElement('div');
    shopRow2.style.cssText = `display: flex; gap: 8px; margin-top: 8px;`;

    const canReroll = this.player.coins >= this._rerollCost;
    const canBlessing = this.player.coins >= this._blessingCost;
    const canAffordHeal = this.player.coins >= SHOP.healCost;
    const isFullHp = this.player.hp >= this.player.maxHp;
    // 钱不够 = 真正禁用；血满但钱够 = 按钮可点击，点击给 toast 提示（避免"按了没反应"）
    const healEnabled = canAffordHeal && !isFullHp;
    const healCostLabel = isFullHp
      ? '生命已满'
      : (canAffordHeal ? `${SHOP.healCost} 金币` : `${SHOP.healCost} 金币（不足）`);

    shopRow1.appendChild(this._buildShopButton(
      `重新抽牌`,
      `${this._rerollCost} 金币`,
      canReroll,
      () => this._doReroll(),
    ));
    shopRow1.appendChild(this._buildShopButton(
      `★ 祝福抽`,
      `${this._blessingCost} 金币 · 必出稀有+`,
      canBlessing,
      () => this._doBlessing(),
    ));
    shopRow2.appendChild(this._buildShopButton(
      `恢复 ${SHOP.healAmount} 生命`,
      healCostLabel,
      healEnabled,
      () => this._doHeal(),
      // 血满时点一下：显示 toast 解释，不消耗金币
      isFullHp ? () => this._showShopToast('生命值已满，无需恢复') : undefined,
    ));
    panel.appendChild(shopRow1);
    panel.appendChild(shopRow2);

    return overlay;
  }

  /** 升级面板内的临时提示（顶部一个绿色小条，1.5s 后淡出） */
  private _showShopToast(text: string): void {
    if (!this._panelEl) return;
    // 同一时间只允许一个 toast，避免连按堆叠
    const existing = this._panelEl.querySelector('[data-shop-toast]');
    if (existing && existing.parentNode) existing.parentNode.removeChild(existing);

    const toast = document.createElement('div');
    toast.setAttribute('data-shop-toast', '1');
    toast.textContent = text;
    toast.style.cssText = `
      position: absolute; top: 14px; left: 50%; transform: translateX(-50%);
      padding: 8px 16px;
      border-radius: 999px;
      background: rgba(255,224,102,.18);
      border: 1px solid rgba(255,224,102,.55);
      color: #ffe066;
      font-size: 13px; font-weight: 600;
      pointer-events: none;
      opacity: 0;
      transition: opacity .15s ease;
    `;
    this._panelEl.appendChild(toast);
    requestAnimationFrame(() => { toast.style.opacity = '1'; });
    setTimeout(() => {
      toast.style.opacity = '0';
      setTimeout(() => { if (toast.parentNode) toast.parentNode.removeChild(toast); }, 200);
    }, 1500);
  }

  /**
   * @param onDisabledClick 可选：按钮 disabled 状态下点击的回调（解释为什么不能点）
   * 注意 disabled 的 <button> 本身不发 click，所以这里包一层 wrapper 接收事件
   */
  private _buildShopButton(
    label: string,
    costLabel: string,
    enabled: boolean,
    onClick: () => void,
    onDisabledClick?: () => void,
  ): HTMLElement {
    // 用 wrapper 包住按钮，让 disabled 时也能接收 click（disabled <button> 不触发事件）
    const wrap = document.createElement('div');
    wrap.style.cssText = `flex: 1; position: relative;`;

    const btn = document.createElement('button');
    btn.type = 'button';
    btn.disabled = !enabled;
    btn.style.cssText = `
      width: 100%; padding: 10px;
      border-radius: 8px;
      border: 1px solid rgba(255,224,102,.5);
      background: ${enabled ? 'rgba(255,224,102,.10)' : 'rgba(120,120,120,.10)'};
      color: ${enabled ? '#ffe066' : '#888'};
      font-size: 13px;
      cursor: ${enabled ? 'pointer' : 'not-allowed'};
    `;
    btn.innerHTML = `
      <div style="font-weight:600;">${label}</div>
      <div style="font-size:12px; opacity:.85; margin-top:2px;">${costLabel}</div>
    `;
    if (enabled) {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        onClick();
      });
    }
    wrap.appendChild(btn);

    // disabled 状态下需要一个透明覆盖层接收点击（disabled 按钮不响应）
    if (!enabled && onDisabledClick) {
      const cover = document.createElement('div');
      cover.style.cssText = `
        position: absolute; inset: 0;
        cursor: pointer;
      `;
      cover.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        onDisabledClick();
      });
      wrap.appendChild(cover);
    }

    return wrap;
  }
}
